import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  BsChevronDown,
  BsChevronRight,
  BsDownload,
  BsEye,
  BsFileEarmarkPlus,
  BsInfoCircle,
  BsSearch,
  BsSend,
  BsThreeDotsVertical,
} from "react-icons/bs";
import procurementApi from "../services/procurementApi";
import memoApi from "../services/memoApi";

// TEMP: all statuses eligible
const ELIGIBLE_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "REJECTED",
  "SENT_BACK",
  "PO_SELECTED",
];

const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;

const formatDateForInput = (date = new Date()) => {
  const d = new Date(date);
  return d.toISOString().slice(0, 10);
};

const extractMemoPayload = (responsePayload) => {
  if (!responsePayload) {
    return null;
  }

  if (responsePayload?.data?.data) {
    return responsePayload.data.data;
  }

  if (responsePayload?.data) {
    return responsePayload.data;
  }

  return responsePayload;
};

const InterOfficeMemo = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [requestRows, setRequestRows] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [serverPagination, setServerPagination] = useState({
    page: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [actionLoadingByRequest, setActionLoadingByRequest] = useState({});
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await procurementApi.getRequests({
        page,
        limit: pageSize,
        search: debouncedSearch || undefined,
      });
      const requests = response?.data || [];
      const pagination = response?.pagination || {};

      setServerPagination({
        page: Number(pagination.page) || page,
        totalPages: Number(pagination.totalPages ?? pagination.pages) || 1,
        totalItems:
          Number(
            pagination.totalItems ?? pagination.total ?? pagination.totalCount,
          ) || 0,
      });

      const eligibleRequests = requests.filter((req) =>
        ELIGIBLE_STATUSES.includes(req.status),
      );

      const memoResults = await Promise.allSettled(
        eligibleRequests.map((req) => memoApi.getMemoByRequestId(req._id)),
      );

      const merged = eligibleRequests.map((req, idx) => {
        const memoResult = memoResults[idx];
        const memo =
          memoResult?.status === "fulfilled"
            ? extractMemoPayload(memoResult.value)
            : null;

        return {
          request: req,
          memo,
        };
      });

      setRequestRows(merged);
    } catch (error) {
      toast({
        title: "Failed to load memo report",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, pageSize, toast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedSearch = search.trim();
      const nextSearchValue =
        trimmedSearch.length === 0 || trimmedSearch.length >= MIN_SEARCH_LENGTH
          ? trimmedSearch
          : "";

      setDebouncedSearch((prev) =>
        prev === nextSearchValue ? prev : nextSearchValue,
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage((prev) => (prev === 1 ? prev : 1));
  }, [debouncedSearch, pageSize]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const setRowActionLoading = (requestId, isLoading) => {
    setActionLoadingByRequest((prev) => ({
      ...prev,
      [requestId]: isLoading,
    }));
  };

  const handleGenerateMemo = async (row) => {
    const request = row.request;
    const requestId = request._id;

    // Keep generation simple and deterministic for quick ops from report list.
    const payload = {
      memoNumber: `IOM-${String(requestId).slice(-6).toUpperCase()}`,
      memoDate: formatDateForInput(),
      department: request.department || "General",
      subject: request.title || "Inter Office Memo",
    };

    try {
      setRowActionLoading(requestId, true);
      await memoApi.generateMemo(requestId, payload);
      toast({
        title: row.memo ? "IOM re-generated" : "IOM generated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      await fetchRows();
    } catch (error) {
      toast({
        title: "Failed to generate IOM",
        description:
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setRowActionLoading(requestId, false);
    }
  };

  const toggleVersionHistory = useCallback(
    async (requestId) => {
      if (expandedRequest === requestId) {
        setExpandedRequest(null);
        return;
      }
      try {
        setVersionsLoading(true);
        setExpandedRequest(requestId);
        const response = await memoApi.getMemoVersions(requestId);
        setVersionHistory(response?.data || []);
      } catch {
        setVersionHistory([]);
      } finally {
        setVersionsLoading(false);
      }
    },
    [expandedRequest],
  );

  const handleDownloadMemo = async (memoId) => {
    try {
      const response = await memoApi.downloadMemoPdf(memoId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition =
        response.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^\"]+)"?/i);
      const fileName = fileNameMatch?.[1] || `IOM-${memoId}.pdf`;

      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Failed to download IOM",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePreviewMemo = async (memoId) => {
    try {
      setPreviewLoading(true);
      const response = await memoApi.downloadMemoPdf(memoId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Revoke after a delay to let the new tab load
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch (error) {
      toast({
        title: "Failed to preview IOM",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleResendMemo = async (row, mode) => {
    const { request } = row;
    const requestId = request?._id;

    if (!requestId) {
      return;
    }

    const recipientsInput = window.prompt(
      "Enter recipient email(s) separated by commas",
      "",
    );

    if (!recipientsInput) {
      return;
    }

    const recipientEmails = recipientsInput
      .split(",")
      .map((email) => email.trim())
      .filter(Boolean);

    if (recipientEmails.length === 0) {
      toast({
        title: "No recipients provided",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const isNewRevision = mode === "NEW_REVISION";
    const shouldProceed = window.confirm(
      isNewRevision
        ? "Create and send a NEW IOM revision to selected recipients?"
        : "Send EXISTING latest IOM revision to selected recipients?",
    );

    if (!shouldProceed) {
      return;
    }

    const fallbackMemoNumber = `IOM-${String(requestId).slice(-6).toUpperCase()}`;
    const payload = {
      mode,
      recipientEmails,
      memoMetadata: {
        memoNumber: row.memo?.memoNumber || fallbackMemoNumber,
        memoDate: formatDateForInput(),
        department: request.department || "General",
        unit: request.unit || "",
        subject: request.title || "Inter Office Memo",
      },
    };

    try {
      setRowActionLoading(requestId, true);
      await memoApi.resendMemo(requestId, payload);
      toast({
        title: isNewRevision
          ? "IOM new revision sent"
          : "IOM existing revision sent",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchRows();
    } catch (error) {
      toast({
        title: "Failed to resend IOM",
        description:
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setRowActionLoading(requestId, false);
    }
  };

  const filteredRows = useMemo(() => {
    return requestRows.filter(({ memo }) => {
      const memoState = memo ? "generated" : "pending";
      const matchesStatus =
        statusFilter === "all" ? true : memoState === statusFilter;

      return matchesStatus;
    });
  }, [requestRows, statusFilter]);

  const canGoPrev = page > 1;
  const canGoNext = page < serverPagination.totalPages;

  const rangeStart =
    serverPagination.totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, serverPagination.totalItems);

  const stats = useMemo(() => {
    const totalEligible = requestRows.length;
    const generated = requestRows.filter((r) => Boolean(r.memo)).length;
    const pending = totalEligible - generated;
    return { totalEligible, generated, pending };
  }, [requestRows]);

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="120px" mb={4} />
        <Skeleton height="420px" />
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
        mb={6}
      >
        <Heading size={{ base: "md", md: "lg" }}>
          IOM (Inter office memo) Print
        </Heading>
        <HStack>
          <Button
            variant="outline"
            onClick={fetchRows}
            size={{ base: "sm", md: "md" }}
          >
            Refresh
          </Button>
          <IconButton
            variant="outline"
            icon={<BsInfoCircle />}
            aria-label="Toggle user guide"
            size={{ base: "sm", md: "md" }}
            onClick={() => setShowGuide((prev) => !prev)}
          />
        </HStack>
      </Flex>

      <Collapse in={showGuide} animateOpacity>
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
          mb={6}
        >
          <Text fontWeight="semibold" mb={2}>
            User Guide
          </Text>
          <VStack align="start" spacing={1} fontSize="sm" color="gray.600">
            <Text>
              1. Use Search and Status filters to find requests and memo
              records.
            </Text>
            <Text>
              2. Generate creates an IOM for requests without a memo,
              Re-generate creates a new version.
            </Text>
            <Text>
              3. Resend Existing emails the latest memo; Resend New Revision
              generates and sends a fresh version.
            </Text>
            <Text>
              4. Preview opens the PDF in a new tab, and Download saves the
              selected memo version.
            </Text>
            <Text>
              5. Use the row arrow to view version history and the page controls
              to move between server pages.
            </Text>
          </VStack>
        </Box>
      </Collapse>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <Stat>
            <StatLabel>Eligible Requests</StatLabel>
            <StatNumber>{stats.totalEligible}</StatNumber>
          </Stat>
        </Box>
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <Stat>
            <StatLabel>Generated IOMs</StatLabel>
            <StatNumber color="green.500">{stats.generated}</StatNumber>
          </Stat>
        </Box>
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <Stat>
            <StatLabel>Pending IOMs</StatLabel>
            <StatNumber color="orange.500">{stats.pending}</StatNumber>
          </Stat>
        </Box>
      </SimpleGrid>

      <Stack direction={{ base: "column", md: "row" }} spacing={3} mb={6}>
        <InputGroup maxW={{ base: "full", md: "360px" }}>
          <InputLeftElement pointerEvents="none">
            <BsSearch />
          </InputLeftElement>
          <Input
            placeholder="Search request, number, department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>

        <Select
          maxW={{ base: "full", md: "220px" }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="generated">Memo Generated</option>
          <option value="pending">Memo Pending</option>
        </Select>

        <Select
          maxW={{ base: "full", md: "160px" }}
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          <option value={10}>10 / page</option>
          <option value={20}>20 / page</option>
          <option value={50}>50 / page</option>
        </Select>
      </Stack>

      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        gap={2}
        mb={4}
      >
        <Text fontSize="sm" color="gray.500">
          Showing {rangeStart}-{rangeEnd} of {serverPagination.totalItems}{" "}
          requests
        </Text>
        <HStack spacing={2}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            isDisabled={!canGoPrev || loading}
          >
            Previous
          </Button>
          <Text fontSize="sm" minW="90px" textAlign="center">
            Page {page} / {serverPagination.totalPages}
          </Text>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              setPage((prev) => Math.min(serverPagination.totalPages, prev + 1))
            }
            isDisabled={!canGoNext || loading}
          >
            Next
          </Button>
        </HStack>
      </Flex>

      {/* Mobile Card View */}
      <VStack spacing={4} display={{ base: "flex", md: "none" }}>
        {filteredRows.length === 0 ? (
          <Box
            bg={bgColor}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={8}
            textAlign="center"
            w="full"
          >
            <Text color="gray.500">
              No memo records found for current filter.
            </Text>
          </Box>
        ) : (
          filteredRows.map(({ request, memo }) => {
            const requestId = request._id;
            const busy = Boolean(actionLoadingByRequest[requestId]);
            const isExpanded = expandedRequest === requestId;
            return (
              <Card
                key={requestId}
                w="full"
                bg={bgColor}
                borderWidth="1px"
                borderColor={borderColor}
              >
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="flex-start">
                    <Box flex="1">
                      <Text fontWeight="semibold" fontSize="sm" noOfLines={2}>
                        {request.title || "Untitled Request"}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {request.requestNumber || requestId}
                      </Text>
                    </Box>
                    {memo && (
                      <IconButton
                        aria-label="Toggle versions"
                        icon={
                          <Icon
                            as={isExpanded ? BsChevronDown : BsChevronRight}
                          />
                        }
                        size="xs"
                        variant="ghost"
                        onClick={() => toggleVersionHistory(requestId)}
                        ml={2}
                      />
                    )}
                  </Flex>
                </CardHeader>
                <CardBody pt={2}>
                  <VStack spacing={3} align="stretch">
                    <SimpleGrid columns={2} spacing={2} fontSize="sm">
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Department
                        </Text>
                        <Text fontWeight="medium">
                          {request.department || "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Status
                        </Text>
                        <Badge colorScheme={memo ? "green" : "orange"}>
                          {memo ? "Generated" : "Pending"}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Memo Number
                        </Text>
                        <Text fontWeight="medium">
                          {memo?.memoNumber || "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Version
                        </Text>
                        <Text fontWeight="medium">{memo?.version || "-"}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Generated By
                        </Text>
                        <Text fontWeight="medium" noOfLines={1}>
                          {memo?.createdBy?.name || "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">
                          Memo Date
                        </Text>
                        <Text fontWeight="medium">
                          {memo?.memoDate
                            ? new Date(memo.memoDate).toLocaleDateString()
                            : "-"}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Divider />

                    <VStack spacing={2} align="stretch">
                      <Button
                        size="sm"
                        leftIcon={<BsFileEarmarkPlus />}
                        colorScheme={memo ? "blue" : "teal"}
                        variant={memo ? "outline" : "solid"}
                        onClick={() => handleGenerateMemo({ request, memo })}
                        isLoading={busy}
                        w="full"
                      >
                        {memo ? "Re-generate" : "Generate"}
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<BsSend />}
                        variant="outline"
                        isDisabled={busy}
                        onClick={() =>
                          handleResendMemo({ request, memo }, "EXISTING")
                        }
                        w="full"
                      >
                        Resend Existing
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<BsSend />}
                        variant="outline"
                        isDisabled={busy}
                        onClick={() =>
                          handleResendMemo({ request, memo }, "NEW_REVISION")
                        }
                        w="full"
                      >
                        Resend New Revision
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<BsEye />}
                        variant="outline"
                        isDisabled={!memo?._id || busy}
                        isLoading={previewLoading}
                        onClick={() => handlePreviewMemo(memo._id)}
                        w="full"
                      >
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<BsDownload />}
                        variant="outline"
                        isDisabled={!memo?._id || busy}
                        onClick={() => handleDownloadMemo(memo._id)}
                        w="full"
                      >
                        Download
                      </Button>
                    </VStack>

                    {isExpanded && memo && (
                      <Collapse in={isExpanded} animateOpacity>
                        <Box bg="gray.50" p={3} borderRadius="md" mt={2}>
                          {versionsLoading ? (
                            <Skeleton height="40px" />
                          ) : versionHistory.length === 0 ? (
                            <Text fontSize="sm" color="gray.500">
                              No version history found.
                            </Text>
                          ) : (
                            <VStack spacing={3} align="stretch">
                              <Text fontSize="sm" fontWeight="semibold">
                                Version History
                              </Text>
                              {versionHistory.map((v) => (
                                <Card key={v._id} size="sm" bg="white">
                                  <CardBody>
                                    <VStack spacing={2} align="stretch">
                                      <SimpleGrid
                                        columns={2}
                                        spacing={2}
                                        fontSize="xs"
                                      >
                                        <Box>
                                          <Text color="gray.500">Version</Text>
                                          <Text fontWeight="medium">
                                            v{v.version}
                                          </Text>
                                        </Box>
                                        <Box>
                                          <Text color="gray.500">Date</Text>
                                          <Text fontWeight="medium">
                                            {v.memoDate
                                              ? new Date(
                                                  v.memoDate,
                                                ).toLocaleDateString()
                                              : "-"}
                                          </Text>
                                        </Box>
                                        <Box gridColumn="span 2">
                                          <Text color="gray.500">
                                            Memo Number
                                          </Text>
                                          <Text fontWeight="medium">
                                            {v.memoNumber}
                                          </Text>
                                        </Box>
                                        <Box gridColumn="span 2">
                                          <Text color="gray.500">
                                            Generated By
                                          </Text>
                                          <Text fontWeight="medium">
                                            {v.createdBy?.name || "-"}
                                          </Text>
                                        </Box>
                                      </SimpleGrid>
                                      <HStack spacing={2}>
                                        <Button
                                          size="xs"
                                          leftIcon={<BsEye />}
                                          variant="outline"
                                          onClick={() =>
                                            handlePreviewMemo(v._id)
                                          }
                                          flex="1"
                                        >
                                          Preview
                                        </Button>
                                        <Button
                                          size="xs"
                                          leftIcon={<BsDownload />}
                                          variant="outline"
                                          onClick={() =>
                                            handleDownloadMemo(v._id)
                                          }
                                          flex="1"
                                        >
                                          Download
                                        </Button>
                                      </HStack>
                                    </VStack>
                                  </CardBody>
                                </Card>
                              ))}
                            </VStack>
                          )}
                        </Box>
                      </Collapse>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            );
          })
        )}
      </VStack>

      {/* Desktop Table View */}
      <Box
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflowX="auto"
        display={{ base: "none", md: "block" }}
      >
        <Table
          variant="simple"
          size="sm"
          tableLayout="fixed"
          w="100%"
          sx={{
            th: { py: 3, whiteSpace: "nowrap" },
            td: { py: 3, verticalAlign: "middle" },
          }}
        >
          <Thead>
            <Tr>
              <Th w="13%">IOM Number</Th>
              <Th w="17%">Title</Th>
              <Th w="12%">Department</Th>
              <Th w="8%">Priority</Th>
              <Th w="10%">Status</Th>
              <Th w="12%">Current Approver</Th>
              <Th w="12%">Last Action By</Th>
              <Th w="8%">Created Date</Th>
              <Th w="8%">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredRows.length === 0 ? (
              <Tr>
                <Td colSpan={9} py={8} textAlign="center">
                  <Text color="gray.500">
                    No memo records found for current filter.
                  </Text>
                </Td>
              </Tr>
            ) : (
              filteredRows.map(({ request, memo }) => {
                const requestId = request._id;
                const busy = Boolean(actionLoadingByRequest[requestId]);
                const isExpanded = expandedRequest === requestId;
                return (
                  <React.Fragment key={requestId}>
                    <Tr>
                      <Td fontWeight="semibold" fontSize="sm">
                        <HStack spacing={1} align="center">
                          <Text noOfLines={1}>{memo?.memoNumber || "-"}</Text>
                          {memo && (
                            <IconButton
                              aria-label="Toggle versions"
                              icon={
                                <Icon
                                  as={
                                    isExpanded ? BsChevronDown : BsChevronRight
                                  }
                                />
                              }
                              size="xs"
                              variant="ghost"
                              onClick={() => toggleVersionHistory(requestId)}
                            />
                          )}
                        </HStack>
                      </Td>
                      <Td>{request.title || "Untitled Request"}</Td>
                      <Td>{request.department || "-"}</Td>
                      <Td>
                        {request.priority ? (
                          <Badge
                            colorScheme={
                              request.priority === "URGENT"
                                ? "red"
                                : request.priority === "HIGH"
                                  ? "orange"
                                  : request.priority === "MEDIUM"
                                    ? "blue"
                                    : "gray"
                            }
                          >
                            {request.priority}
                          </Badge>
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            N/A
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            request.status === "APPROVED"
                              ? "green"
                              : request.status === "REJECTED"
                                ? "red"
                                : request.status === "SENT_BACK"
                                  ? "yellow"
                                  : "blue"
                          }
                        >
                          {request.status || "-"}
                        </Badge>
                      </Td>
                      <Td>{request.currentApprover?.name || "-"}</Td>
                      <Td>
                        {request.updatedBy?.name ||
                          request.createdBy?.name ||
                          "-"}
                      </Td>
                      <Td>
                        {request.createdAt
                          ? new Date(request.createdAt).toLocaleDateString()
                          : "-"}
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton
                            size="xs"
                            icon={<BsEye />}
                            variant="ghost"
                            aria-label="Preview latest memo"
                            isDisabled={!memo?._id || busy}
                            isLoading={previewLoading}
                            onClick={() => handlePreviewMemo(memo._id)}
                          />
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<BsThreeDotsVertical />}
                              size="xs"
                              variant="ghost"
                              aria-label="More actions"
                              isDisabled={busy}
                            />
                            <Portal>
                              <MenuList>
                                <MenuItem
                                  icon={<BsFileEarmarkPlus />}
                                  onClick={() =>
                                    handleGenerateMemo({ request, memo })
                                  }
                                >
                                  {memo ? "Re-generate" : "Generate"}
                                </MenuItem>
                                <MenuItem
                                  icon={<BsSend />}
                                  onClick={() =>
                                    handleResendMemo(
                                      { request, memo },
                                      "EXISTING",
                                    )
                                  }
                                >
                                  Resend Existing
                                </MenuItem>
                                <MenuItem
                                  icon={<BsSend />}
                                  onClick={() =>
                                    handleResendMemo(
                                      { request, memo },
                                      "NEW_REVISION",
                                    )
                                  }
                                >
                                  Resend New Revision
                                </MenuItem>
                                <MenuItem
                                  icon={<BsDownload />}
                                  isDisabled={!memo?._id}
                                  onClick={() => handleDownloadMemo(memo._id)}
                                >
                                  Download Latest
                                </MenuItem>
                                {memo && (
                                  <MenuItem
                                    icon={
                                      <Icon
                                        as={
                                          isExpanded
                                            ? BsChevronDown
                                            : BsChevronRight
                                        }
                                      />
                                    }
                                    onClick={() =>
                                      toggleVersionHistory(requestId)
                                    }
                                  >
                                    {isExpanded
                                      ? "Hide Versions"
                                      : "Show Versions"}
                                  </MenuItem>
                                )}
                              </MenuList>
                            </Portal>
                          </Menu>
                        </HStack>
                      </Td>
                    </Tr>
                    {isExpanded && memo && (
                      <Tr>
                        <Td colSpan={9} p={0} borderBottom="none">
                          <Collapse in={isExpanded} animateOpacity>
                            <Box bg="gray.50" px={6} py={3}>
                              {versionsLoading ? (
                                <Skeleton height="40px" />
                              ) : versionHistory.length === 0 ? (
                                <Text fontSize="sm" color="gray.500">
                                  No version history found.
                                </Text>
                              ) : (
                                <Table size="xs" variant="simple">
                                  <Thead>
                                    <Tr>
                                      <Th fontSize="xs">Version</Th>
                                      <Th fontSize="xs">Memo Number</Th>
                                      <Th fontSize="xs">Generated By</Th>
                                      <Th fontSize="xs">Date</Th>
                                      <Th fontSize="xs">Actions</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {versionHistory.map((v) => (
                                      <Tr key={v._id}>
                                        <Td fontSize="sm">v{v.version}</Td>
                                        <Td fontSize="sm">{v.memoNumber}</Td>
                                        <Td fontSize="sm">
                                          {v.createdBy?.name || "-"}
                                        </Td>
                                        <Td fontSize="sm">
                                          {v.memoDate
                                            ? new Date(
                                                v.memoDate,
                                              ).toLocaleDateString()
                                            : "-"}
                                        </Td>
                                        <Td>
                                          <VStack spacing={2} align="stretch">
                                            <Button
                                              size="xs"
                                              leftIcon={<BsEye />}
                                              variant="outline"
                                              onClick={() =>
                                                handlePreviewMemo(v._id)
                                              }
                                            >
                                              Preview
                                            </Button>
                                            <Button
                                              size="xs"
                                              leftIcon={<BsDownload />}
                                              variant="outline"
                                              onClick={() =>
                                                handleDownloadMemo(v._id)
                                              }
                                            >
                                              Download
                                            </Button>
                                          </VStack>
                                        </Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default InterOfficeMemo;
